import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, KeyRound, Loader2, ShieldAlert, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import McpKeysService from '../services/mcpKeysService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Копирует текст в буфер обмена. clipboard API недоступен вне https/localhost —
 * тогда старый способ через скрытое поле.
 * @param {string} text
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    document.body.removeChild(field);
  }
}

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const isExpired = (iso) => new Date(iso).getTime() <= Date.now();

/** Строка списка ключей: подпись, даты, отзыв */
const KeyRow = ({ item, onRevoke, revoking }) => {
  const expired = isExpired(item.expiresAt);
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 text-sm">
        <div className="truncate font-medium">{item.label}</div>
        <div className="text-xs text-muted-foreground">
          Создан {formatDate(item.createdAt)} · использован {item.lastUsedAt ? formatDate(item.lastUsedAt) : 'ещё не использовался'}
        </div>
        <div className={`text-xs ${expired ? 'text-destructive' : 'text-muted-foreground'}`}>
          {expired ? `Истёк ${formatDay(item.expiresAt)}` : `Действует до ${formatDay(item.expiresAt)}`}
        </div>
        {(item.issuedIp || item.issuedUserAgent) && (
          <div className="truncate text-xs text-muted-foreground" title={item.issuedUserAgent || ''}>
            Выпущен с {item.issuedIp || 'неизвестного IP'}
            {item.issuedUserAgent ? ` · ${item.issuedUserAgent}` : ''}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRevoke(item)}
        disabled={revoking}
        title="Отозвать ключ"
        aria-label={`Отозвать ключ ${item.label}`}
      >
        {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
      </Button>
    </li>
  );
};

/**
 * Главное предупреждение про утечку ключа: чат LLM уходит провайдеру модели и
 * остаётся в истории сессии, а `!команда` в Claude Code тоже кладёт строку в чат.
 * Явные red-* вместо destructive: в тёмной теме --destructive слишком тусклый.
 */
const NoChatWarning = () => (
  <div
    role="alert"
    className="rounded-md border-2 border-red-600 bg-red-50 p-3 text-red-800 dark:border-red-500 dark:bg-red-950/60 dark:text-red-200"
  >
    <div className="flex items-start gap-3">
      <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-red-600 dark:text-red-400" />
      <div className="space-y-1.5">
        <p className="text-base font-bold uppercase tracking-wide">
          Нельзя вставлять ключ или команду в чат с ИИ
        </p>
        <p className="text-sm">
          Ни в Claude Code, ни в ChatGPT, Cursor, Copilot или другой LLM. Не просите ИИ выполнить команду за вас и не
          запускайте её через <code className="font-mono">!</code> в Claude Code — строка с ключом так же попадёт в
          чат. Всё, что попало в чат, уходит провайдеру модели и сохраняется в истории сессии, в том числе файлом на
          диске. Не пересылайте ключ в мессенджерах и не показывайте на скриншотах: любой, кто его увидит, сможет
          читать данные дашборда от вашего имени.
        </p>
        <p className="text-sm font-semibold">
          Попал в чат — сразу отзовите ключ (корзина в списке ниже) и выпустите новый.
        </p>
      </div>
    </div>
  </div>
);

/** Только что выпущенный ключ: готовая команда и предупреждение «один раз» */
const IssuedKey = ({ issued }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = async () => {
    await copyToClipboard(issued.command);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <NoChatWarning />
      <div className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3">
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>
            Ключ показывается <b>один раз</b> — скопируйте команду сейчас и выполните её в обычном терминале
            (bash, zsh, PowerShell или cmd), не в сессии Claude Code. Действует до {formatDay(issued.expiresAt)}.
          </span>
        </div>
        <div className="flex items-start gap-2">
          <code className="block min-w-0 flex-1 select-all break-all rounded bg-muted px-2 py-1.5 font-mono text-xs">
            {issued.command}
          </code>
          <Button variant="outline" size="sm" onClick={copy} aria-label="Скопировать команду">
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Скопировано' : 'Скопировать'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Команда с ключом останется в истории терминала. В bash и zsh начните строку с пробела — тогда она в
          историю не попадёт (bash: при HISTCONTROL=ignorespace или ignoreboth, zsh: при setopt HIST_IGNORE_SPACE).
          В PowerShell <code>Clear-History</code> не чистит историю PSReadLine — удалите строку с ключом из файла{' '}
          <code>(Get-PSReadLineOption).HistorySavePath</code>. Сам Claude Code хранит ключ в файле настроек{' '}
          <code>~/.claude.json</code> — не публикуйте этот файл.
        </p>
      </div>
    </div>
  );
};

/**
 * Диалог «Подключить к Claude Code»: выпуск персонального ключа, команда
 * подключения с копированием, список своих ключей с отзывом.
 */
const McpConnectDialog = ({ open, onOpenChange }) => {
  const { login } = useAuth();
  const [reauthRequired, setReauthRequired] = useState(false);
  const [status, setStatus] = useState(null);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  // Ответ выпуска, пришедший после закрытия диалога, ключ в памяти не оставляет
  const openRef = useRef(open);
  openRef.current = open;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await McpKeysService.getStatus());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      // Значение ключа не держим в памяти дольше, чем открыт диалог
      setIssued(null);
      setError('');
      setLabel('');
      setReauthRequired(false);
      return;
    }
    loadStatus();
  }, [open, loadStatus]);

  const issue = async () => {
    setIssuing(true);
    setError('');
    setReauthRequired(false);
    try {
      const result = await McpKeysService.issueKey(label.trim());
      if (!openRef.current) return;
      setIssued(result);
      setLabel('');
      await loadStatus();
    } catch (err) {
      if (err.reauthRequired) setReauthRequired(true);
      else setError(err.message);
    } finally {
      setIssuing(false);
    }
  };

  const revoke = async (item) => {
    if (!window.confirm(`Отозвать ключ «${item.label}»? Claude Code с этим ключом сразу потеряет доступ.`)) return;
    setRevokingId(item.id);
    setError('');
    try {
      await McpKeysService.revokeKey(item.id);
      if (issued?.id === item.id) setIssued(null);
      await loadStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const keys = status?.keys || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Прокручивается только тело (вторая строка grid), чтобы крестик закрытия не уезжал;
          100vh — запасной вариант для браузеров без dvh */}
      <DialogContent className="max-h-[calc(100vh-2rem)] grid-rows-[auto_minmax(0,1fr)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Подключить к Claude Code</DialogTitle>
          <DialogDescription>
            Персональный ключ даёт Claude Code доступ к данным дашборда от вашего имени — те же разделы, что вы
            видите здесь. Срок жизни ключа — {status?.ttlDays ?? '…'} дн.; перевыпуск — этой же кнопкой. Ключи
            перестают работать, если {status?.idleDays ?? '…'} дн. не входить в дашборд через Google, и снова
            работают после входа.
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-6 -my-1 min-h-0 space-y-4 overflow-y-auto px-6 py-1">
          {status && status.enabled === false && (
            <p className="text-sm text-muted-foreground">Подключение к Claude Code сейчас выключено.</p>
          )}

          {status?.enabled && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Подпись ключа, например «рабочий ноутбук»"
                  maxLength={64}
                  disabled={issuing}
                />
                <Button onClick={issue} disabled={issuing}>
                  {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  Выпустить ключ
                </Button>
              </div>

              {!issued && (
                <p className="flex items-start gap-2 text-sm font-medium text-red-700 dark:text-red-300">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  Ключ — как пароль: не вставляйте его и команду подключения в чат Claude Code или другого ИИ.
                </p>
              )}

              {reauthRequired && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
                  <span>
                    Для выпуска ключа нужен недавний вход через Google
                    {status.reauthMinutes ? ` (не раньше ${status.reauthMinutes} мин. назад)` : ''}. Войдите заново, затем
                    откройте это окно и нажмите «Выпустить ключ» ещё раз.
                  </span>
                  <Button variant="outline" size="sm" onClick={login}>
                    Войти через Google
                  </Button>
                </div>
              )}

              {issued && <IssuedKey issued={issued} />}

              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Ваши ключи{status.maxActiveKeys ? ` (не больше ${status.maxActiveKeys} активных)` : ''}
                </div>
                {keys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ключей пока нет.</p>
                ) : (
                  <ul className="max-h-64 space-y-2 overflow-y-auto">
                    {keys.map((item) => (
                      <KeyRow key={item.id} item={item} onRevoke={revoke} revoking={revokingId === item.id} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {loading && !status && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Загрузка…
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default McpConnectDialog;
